// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { ModalStackProvider, useModalZIndex } from '../contexts/ModalStackContext';
import { useAuth } from '../hooks/useAuth';
import { Customer } from '../lib/customers';
import { CommunicationCard, Message } from '../types/messages';
import { createCommunicationCardFromCustomer } from '../lib/messageUtils';
import ConversationView from './messages/ConversationView';
import CustomerCreateMenu from './messages/CustomerCreateMenu';
import { supabase } from '../lib/supabase';
import { getCustomerMilestones, BookingMilestone } from '../lib/bookingMilestones';
import { isCoreMessagesEnabled, coreMarkItemsRead, coreMarkConversationRead, conversationKeyForCard, type CoreReadKind } from '../lib/messagesCore';
import { getBooking, Booking } from '../lib/bookings';
import { getProposal, Proposal } from '../lib/proposals';
import EditCustomerModal from './manage/EditCustomerModal';
import CustomerModal from './CustomerModal';
import CustomerDetailsModal from './CustomerDetailsModal';
import BookingInfoModal from './BookingInfoModal';
import ProposalDetailsModal from './ProposalDetailsModal';

interface EmailAttachment {
  id: string;
  gmail_id: string;
  filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  public_url: string;
  is_inline: boolean;
}

interface CustomerMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  businessId?: string;
  initialComposerType?: 'sms' | 'email';
}

/**
 * Reusable Customer Message Modal
 * This modal can be opened from anywhere in the app to message a customer
 * It replicates the messages window functionality in a modal format
 */
export default function CustomerMessageModal({
  isOpen,
  onClose,
  customer,
  businessId,
  initialComposerType = 'sms'
}: CustomerMessageModalProps) {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  // Resolve the modal's z-index from the modal stack context so nested modals
  // (Snippets, Customer Details, etc.) automatically stack on top of this one.
  const modalZ = useModalZIndex(100160);
  const [card, setCard] = useState<CommunicationCard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [locallyReadMessages, setLocallyReadMessages] = useState<Set<string>>(new Set());
  const [emailAttachmentsByGmailId, setEmailAttachmentsByGmailId] = useState<Record<string, EmailAttachment[]>>({});
  const [timelineAttachmentsByGmailId, setTimelineAttachmentsByGmailId] = useState<Record<string, EmailAttachment[]>>({});
  const [mutedSenders, setMutedSenders] = useState<Set<string>>(new Set());
  
  // Modal states for nested modals
  const [customerDetailsModalOpen, setCustomerDetailsModalOpen] = useState(false);
  const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState(false);
  const [selectedCustomerForDetails, setSelectedCustomerForDetails] = useState<Customer | null>(null);
  const [createCustomerInitialData, setCreateCustomerInitialData] = useState<{ phone?: string; email?: string }>({});
  const [bookingInfoModalOpen, setBookingInfoModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [proposalDetailsModalOpen, setProposalDetailsModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  
  const activeBusinessId = businessId || selectedBusiness?.id;

  // Fetch customers when modal opens
  useEffect(() => {
    if (!isOpen || !activeBusinessId || !supabase) return;

    const fetchCustomers = async () => {
      try {
        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .eq('business_id', activeBusinessId);

        if (error) throw error;
        setCustomers(data || []);
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
    };

    fetchCustomers();
  }, [isOpen, activeBusinessId]);

  // Load communication card when customer changes
  useEffect(() => {
    if (!isOpen || !customer || !activeBusinessId) {
      setCard(null);
      return;
    }

    const loadCard = async () => {
      setLoading(true);
      setError(null);
      try {
        const communicationCard = await createCommunicationCardFromCustomer(
          customer,
          activeBusinessId
        );
        setCard(communicationCard);
      } catch (err) {
        console.error('Error loading communication card:', err);
        setError(err instanceof Error ? err.message : 'Failed to load messages');
      } finally {
        setLoading(false);
      }
    };

    loadCard();
  }, [isOpen, customer?.id, activeBusinessId]);

  // Load muted email senders
  useEffect(() => {
    if (!activeBusinessId || !supabase) return;

    const loadMutedSenders = async () => {
      try {
        const { data, error } = await supabase
          .from('muted_email_senders')
          .select('email_address')
          .eq('business_id', activeBusinessId);

        if (error) {
          console.error('Error loading muted senders:', error);
          return;
        }

        const mutedSet = new Set<string>((data || []).map(item => item.email_address.toLowerCase().trim()));
        setMutedSenders(mutedSet);
      } catch (error) {
        console.error('Error loading muted senders:', error);
      }
    };

    loadMutedSenders();
  }, [activeBusinessId]);

  // Load email attachments when card messages change
  useEffect(() => {
    if (!activeBusinessId || !supabase || !card) return;
    
    const gmailIds = Array.from(
      new Set(
        card.messages
          .filter((msg) => msg.message_type === 'email' && msg.twilio_message_sid)
          .map((msg) => msg.twilio_message_sid as string)
      )
    );

    if (gmailIds.length === 0) {
      setEmailAttachmentsByGmailId({});
      return;
    }

    const loadAttachments = async () => {
      try {
        const { data, error } = await supabase
          .from('email_attachments')
          .select('id, gmail_id, filename, content_type, size_bytes, public_url, is_inline')
          .eq('business_id', activeBusinessId)
          .in('gmail_id', gmailIds);

        if (error) throw error;

        const grouped = (data || []).reduce<Record<string, EmailAttachment[]>>((acc, attachment) => {
          if (!acc[attachment.gmail_id]) acc[attachment.gmail_id] = [];
          acc[attachment.gmail_id].push(attachment);
          return acc;
        }, {});

        setEmailAttachmentsByGmailId(grouped);
      } catch (err) {
        console.error('Error loading email attachments:', err);
      }
    };

    loadAttachments();
  }, [card?.messages, activeBusinessId]);

  // Real-time subscription for muted senders
  useEffect(() => {
    if (!activeBusinessId || !supabase) return;

    const channel = supabase
      .channel(`muted_email_senders_changes_${activeBusinessId}_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'muted_email_senders',
          filter: `business_id=eq.${activeBusinessId}`
        },
        async () => {
          // Reload muted senders
          try {
            const { data, error } = await supabase
              .from('muted_email_senders')
              .select('email_address')
              .eq('business_id', activeBusinessId);

            if (error) {
              console.error('Error reloading muted senders:', error);
              return;
            }

            const mutedSet = new Set<string>((data || []).map(item => item.email_address.toLowerCase().trim()));
            setMutedSenders(mutedSet);
          } catch (error) {
            console.error('Error reloading muted senders:', error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeBusinessId]);

  // Real-time subscription for email attachments
  useEffect(() => {
    if (!activeBusinessId || !supabase) return;

    const channel = supabase
      .channel(`email_attachments_changes_${activeBusinessId}_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'email_attachments',
          filter: `business_id=eq.${activeBusinessId}`
        },
        async () => {
          // Reload attachments if card exists
          if (!card) return;
          
          const gmailIds = Array.from(
            new Set(
              card.messages
                .filter((msg) => msg.message_type === 'email' && msg.twilio_message_sid)
                .map((msg) => msg.twilio_message_sid as string)
            )
          );

          if (gmailIds.length === 0) return;

          try {
            const { data, error } = await supabase
              .from('email_attachments')
              .select('id, gmail_id, filename, content_type, size_bytes, public_url, is_inline')
              .eq('business_id', activeBusinessId)
              .in('gmail_id', gmailIds);

            if (error) throw error;

            const grouped = (data || []).reduce<Record<string, EmailAttachment[]>>((acc, attachment) => {
              if (!acc[attachment.gmail_id]) acc[attachment.gmail_id] = [];
              acc[attachment.gmail_id].push(attachment);
              return acc;
            }, {});

            setEmailAttachmentsByGmailId(grouped);
          } catch (err) {
            console.error('Error reloading email attachments:', err);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeBusinessId, card]);

  // Mark message as read
  const markMessageAsRead = async (messageId: string) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Core tables: one call marks the row, whatever kind it is.
      if (activeBusinessId && await isCoreMessagesEnabled(activeBusinessId)) {
        setLocallyReadMessages(prev => new Set([...prev, messageId]));
        try {
          await coreMarkItemsRead(activeBusinessId, [messageId]);
        } catch (error) {
          setLocallyReadMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(messageId);
            return newSet;
          });
          throw error;
        }
        window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
        return;
      }

      // Check if this is a call or a message
      const { data: callData } = await supabase
        .from('call_history')
        .select('id')
        .eq('id', messageId)
        .single();
      
      if (callData) {
        // This is a call - update call_history table
        const { error } = await supabase
          .from('call_history')
          .update({ viewed: true })
          .eq('id', messageId);

        if (error) throw error;
      } else {
        // This is a regular message - update all_messages table
        setLocallyReadMessages(prev => new Set([...prev, messageId]));

        const { error } = await supabase
          .from('all_messages')
          .update({ read: 'read' })
          .eq('id', messageId);

        if (error) {
          setLocallyReadMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(messageId);
            return newSet;
          });
          throw error;
        }
      }
      
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Mark a set of messages read (one email thread, for example).
  const markMessagesAsRead = async (messageIds: string[]) => {
    if (messageIds.length === 0) return;
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }
      setLocallyReadMessages(prev => new Set([...prev, ...messageIds]));
      try {
        if (activeBusinessId && await isCoreMessagesEnabled(activeBusinessId)) {
          await coreMarkItemsRead(activeBusinessId, messageIds);
        } else {
          const { error } = await supabase
            .from('all_messages')
            .update({ read: 'read' })
            .in('id', messageIds);
          if (error) throw error;
        }
      } catch (error) {
        setLocallyReadMessages(prev => {
          const next = new Set(prev);
          messageIds.forEach(id => next.delete(id));
          return next;
        });
        throw error;
      }
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Mark all messages as read. `kinds` narrows it: a text reply passes the
  // phone kinds so unread email threads stay unread.
  const markAllMessagesAsRead = async (card: CommunicationCard, kinds?: CoreReadKind[]) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const kindSet = kinds && kinds.length ? new Set<string>(kinds) : null;
      const unreadMessageIds = card.messages
        .filter(msg => msg.read === 'unread' && msg.direction === 'inbound' && (!kindSet || kindSet.has(msg.message_type)))
        .map(msg => msg.id);

      // Core tables: clear the conversation (or the requested kinds) in one call.
      if (activeBusinessId && await isCoreMessagesEnabled(activeBusinessId)) {
        setLocallyReadMessages(prev => new Set([...prev, ...unreadMessageIds]));
        await coreMarkConversationRead(activeBusinessId, conversationKeyForCard(card), kinds);
        window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
        return;
      }

      if (unreadMessageIds.length === 0) return;

      setLocallyReadMessages(prev => new Set([...prev, ...unreadMessageIds]));

      if (unreadMessageIds.length > 0) {
        const { error } = await supabase
          .from('all_messages')
          .update({ read: 'read' })
          .in('id', unreadMessageIds);

        if (error) {
          setLocallyReadMessages(prev => {
            const newSet = new Set(prev);
            unreadMessageIds.forEach(id => newSet.delete(id));
            return newSet;
          });
          throw error;
        }
      }

      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  // Fetch milestones
  const fetchCustomerMilestones = async (customerId: string, setLoading?: (loading: boolean) => void) => {
    try {
      if (setLoading) setLoading(true);
      await getCustomerMilestones(customerId);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  // Handle viewing customer details (for existing customers)
  const handleViewCustomerDetails = (customer: Customer) => {
    setSelectedCustomerForDetails(customer);
    setCustomerDetailsModalOpen(true);
  };

  // Handle create customer (for non-existing customers)
  const handleCreateCustomer = (card: CommunicationCard) => {
    const initialData: { phone?: string; email?: string } = {};
    
    if (card.type === 'phone') {
      initialData.phone = card.contact_info;
    } else if (card.type === 'email') {
      initialData.email = card.contact_info;
    }
    
    setCreateCustomerInitialData(initialData);
    setCreateCustomerModalOpen(true);
  };

  // Handle customer success
  const handleCustomerSuccess = async (customer: Customer) => {
    if (activeBusinessId && supabase) {
      try {
        const { data: customersData, error } = await supabase
          .from('customers')
          .select('*')
          .eq('business_id', activeBusinessId);

        if (error) throw error;
        setCustomers(customersData || []);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    }
    
    setCustomerDetailsModalOpen(false);
    setCreateCustomerModalOpen(false);
    setSelectedCustomerForDetails(null);
    setCreateCustomerInitialData({});
  };


  // Handle milestone click
  const handleMilestoneClick = async (milestone: BookingMilestone) => {
    try {
      const entityId = milestone.booking_id;
      
      if (milestone.milestone.metadata?.entity_type === 'proposal') {
        const proposal = await getProposal(entityId);
        if (proposal) {
          setSelectedProposal(proposal);
          setProposalDetailsModalOpen(true);
        }
        return;
      }
      
      if (entityId) {
        const booking = await getBooking(entityId);
        if (booking) {
          setSelectedBooking(booking);
          setBookingInfoModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error loading entity from milestone:', error);
    }
  };

  // Extract email address helper
  const extractEmailAddress = (contactInfo: string): string => {
    const emailMatch = contactInfo.match(/<([^>]+)>/);
    return emailMatch ? emailMatch[1] : contactInfo;
  };

  // Function to normalize email address (extract just the email from "Name <email@domain.com>")
  const normalizeEmail = (email: string): string => {
    if (!email) return '';
    // Extract email from "Name <email@domain.com>" format
    const match = email.match(/<([^>]+)>/);
    if (match) {
      return match[1].toLowerCase().trim();
    }
    return email.toLowerCase().trim();
  };

  // Function to mute an email sender
  const muteEmailSender = async (emailAddress: string) => {
    if (!activeBusinessId || !supabase || !user?.id) return;

    const normalizedEmail = normalizeEmail(emailAddress);
    
    try {
      const { error } = await supabase
        .from('muted_email_senders')
        .insert({
          business_id: activeBusinessId,
          email_address: normalizedEmail,
          muted_by: user.id,
          reason: 'junk'
        });

      if (error) {
        // If already muted, that's fine
        if (error.code !== '23505') { // Unique constraint violation
          console.error('Error muting sender:', error);
          throw error;
        }
      }

      // Update local state
      setMutedSenders(prev => new Set([...prev, normalizedEmail]));
      
      console.log('✅ Muted email sender:', normalizedEmail);
    } catch (error) {
      console.error('Error muting email sender:', error);
      throw error;
    }
  };

  // Function to unmute an email sender
  const unmuteEmailSender = async (emailAddress: string) => {
    if (!activeBusinessId || !supabase) return;

    const normalizedEmail = normalizeEmail(emailAddress);
    
    try {
      const { error } = await supabase
        .from('muted_email_senders')
        .delete()
        .eq('business_id', activeBusinessId)
        .eq('email_address', normalizedEmail);

      if (error) {
        console.error('Error unmuting sender:', error);
        throw error;
      }

      // Update local state
      setMutedSenders(prev => {
        const newSet = new Set(prev);
        newSet.delete(normalizedEmail);
        return newSet;
      });
      
      console.log('✅ Unmuted email sender:', normalizedEmail);
    } catch (error) {
      console.error('Error unmuting email sender:', error);
      throw error;
    }
  };

  // Listen for global event to close modal
  useEffect(() => {
    const handleCloseAllModals = () => {
      if (isOpen) {
        onClose();
      }
    };

    window.addEventListener('closeAllModals', handleCloseAllModals);
    
    return () => {
      window.removeEventListener('closeAllModals', handleCloseAllModals);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <ModalStackProvider zIndex={modalZ}>
      {isOpen && (
      <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity duration-200"
        style={{ zIndex: modalZ }}
        onClick={onClose}
      />
      {/* Right-side mounted panel – full screen on mobile, side panel on desktop */}
      <div
        className="fixed inset-y-0 right-0 w-full sm:w-[560px] bg-white shadow-2xl flex flex-col transform transition-transform duration-200 translate-x-0"
        style={{
          zIndex: modalZ,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Header Controls: blue + dropdown + close button */}
        <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
          <CustomerCreateMenu customer={customer} />
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-[0.98]"
            title="Close"
          >
            <X className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden h-full w-full">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading messages...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-red-500 mb-4">{error}</p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Close
                </button>
              </div>
            </div>
          ) : card ? (
            <ConversationView
              card={card}
              markMessageAsRead={markMessageAsRead}
              markMessagesAsRead={markMessagesAsRead}
              locallyReadMessages={locallyReadMessages}
              fetchMilestones={fetchCustomerMilestones}
              onClose={onClose}
              hideCloseButton={true}
              customers={customers}
              onEditCustomer={handleViewCustomerDetails}
              onCreateCustomer={handleCreateCustomer}
              onMilestoneClick={handleMilestoneClick}
              markAllMessagesAsRead={markAllMessagesAsRead}
              extractEmailAddress={extractEmailAddress}
              initialReplyMethod={initialComposerType}
              emailAttachmentsByGmailId={emailAttachmentsByGmailId}
              timelineAttachmentsByGmailId={timelineAttachmentsByGmailId}
              muteEmailSender={muteEmailSender}
              unmuteEmailSender={unmuteEmailSender}
              normalizeEmail={normalizeEmail}
              hideInlineCreateButton
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">No customer selected</p>
            </div>
          )}
        </div>
      </div>

      {/* Nested Modals */}
      {selectedBusiness && (
        <>
          <CustomerDetailsModal
            isOpen={customerDetailsModalOpen}
            onClose={() => {
              setCustomerDetailsModalOpen(false);
              setSelectedCustomerForDetails(null);
            }}
            customer={selectedCustomerForDetails}
            onUpdate={handleCustomerSuccess}
          />
          
          <CustomerModal
            isOpen={createCustomerModalOpen}
            onClose={() => {
              setCreateCustomerModalOpen(false);
              setCreateCustomerInitialData({});
            }}
            onSuccess={handleCustomerSuccess}
            businessId={selectedBusiness.id}
            initialData={createCustomerInitialData}
          />
          
          <BookingInfoModal
            isOpen={bookingInfoModalOpen}
            onClose={() => {
              setBookingInfoModalOpen(false);
              setSelectedBooking(null);
            }}
            booking={selectedBooking}
            onUpdate={() => {}}
            readOnly={false}
          />
          
          {selectedProposal && (
            <ProposalDetailsModal
              isOpen={proposalDetailsModalOpen}
              onClose={() => {
                setProposalDetailsModalOpen(false);
                setSelectedProposal(null);
              }}
              proposal={selectedProposal}
              onUpdate={() => {}}
            />
          )}
        </>
      )}
      </>
      )}

    </ModalStackProvider>,
    document.body
  );
}

